import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typeahead, Menu, MenuItem, Highlighter } from 'react-bootstrap-typeahead';
import { format, sub, startOfMonth, addMonths } from 'date-fns';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-calendar/dist/Calendar.css';
import './assets/styles/CompanyKPIContainer.scss';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import {
  getPaidRegisteredUsers,
  GetActiveUsers,
  GetPaidCorporateList,
  fetchTotalUsersPlayedContent,
  fetchUsersRatedContent,
  fetchUsersFavoriteTheContent,
  fetchNumberOfPcCompleted,
  fetchNumberOfPcBookings,
  fetchNumberOfUsersTakeWater,
  fetchTotalEarnedRewardPoints,
  fetchTotalUsersEarnedRewards,
  fetchTotalUsersRedeemedRewards,
  fetchTotalPushSent,
  fetchTotalChallengesLaunched,
  fetchTotalUsersJoinedInChallenge,
  fetchTotalUsersParticipatedInChallenge,
  fetchProgramJoined,
  fetchProgramCompleted,
  fetchTotalMinsPlayed,
  fetchTotalStepsCaptured,
  fetchTotalUsersSyncingSteps,
  fetchBookRead,
  saveCurrentDateRange,
  resetKpiState,
  downloadOverallReportForAllEnterprise,
  saveSelectedCompany,
  saveDateRange12Months,
  saveDateRange90Days,
  saveDateRange30Days,
  hardResetKpiState,
  fetchTotalRedeemedRewardPoints,
  saveCropIdNameMap,
  GetFreemiumCorporateList,
  saveFreemiumCropIdNameMap,
  clearKpiState,
  fetchTotalMealImagesUploaded,
  fetchTotalMealsAdded,
  fetchTotalAICoachMessages,
  fetchUniqueNutritionUsers,
} from './store/actions';

import WCLogo from '../../assets/images/wc-logo';
import {
  downloadTableAsExcel,
  getDateFromString,
  getModifiedStartEndDate,
} from '../../utils/helpers';
import { useConfirm } from '../commons/components';
import { toast } from 'react-toastify';
import { DateFilter } from './components';
import { Badge } from 'react-bootstrap';
import Last90DaysContainer from './container/Last90DaysContainer';
import Last30DaysContainer from './container/Last30DaysContainer';
import Last12MonthsContainer from './container/Last12MonthsContainer';
import { setAppVersion } from '../auth/store/actions';

function CompanyKPIContainer() {
  const DEFAULT_RANGE_MONTHS = 6;
  const now = new Date();
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });

  const [startDate, setStartDate] = useState(format(startDateDate, 'LLL d, yyyy'));
  const [endDate, setEndDate] = useState(format(endDateDate, 'LLL d, yyyy'));
  const [startValue, setStartValue] = useState();
  const [endValue, setEndValue] = useState();

  const [companySelected, setCompanySelected] = useState([]);
  const dispatch = useDispatch();

  const last12Months = useSelector((state) => state.kpi.last12Months);
  const last90Days = useSelector((state) => state.kpi.last90Days);
  const last30Days = useSelector((state) => state.kpi.last30Days);
  const savedDateRange = useSelector((state) => state.kpi.currentDateRange);
  const selectedCompany = useSelector((state) => state.kpi.selectedCompany);
  const corporateList = useSelector((state) => state.kpi.corporateList);
  const freemiumCorporateList = useSelector((state) => state.kpi.freemiumCorporateList);
  const pdfLoading = useSelector((state) => state.kpi.pdfLoading);
  const auth = useSelector((state) => state.auth);

  const [copyCorporateList, setCopyCorporateList] = useState([]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const hardRefresh = useRef(false);
  const dateSetRef = useRef(false);
  const cacheEndDateUpdatedOnce = useRef(false);
  const payloadRef = useRef({});
  const { info } = useConfirm();
  const selectedFilter = useRef();

  function isDownloadInProgress() {
    return pdfLoading;
  }

  function downloadSheet() {
    const tableElement = document.getElementById('data-table-container');
    const rangeText = `${format(getDateFromString(startValue), 'yyyy-MM-dd')} to ${format(
      getDateFromString(endValue),
      'yyyy-MM-dd'
    )}`;
    downloadTableAsExcel(
      tableElement,
      `overview(${rangeText}).xlsx`
    );
  }

  /**
   * get all corporate list
   */
  useEffect(() => {
    dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
    dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
  }, []);

  /**
   * clear all cached data if app version is different
   */
  useEffect(() => {
    if (!auth.appVersion || auth.appVersion !== process.env.REACT_APP_VERSION) {
      dispatch(setAppVersion(process.env.REACT_APP_VERSION));
      dispatch(hardResetKpiState());
      setTimeout(() => {
        dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
        dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
      }, 500);
    }
  }, []);

  /** calculate current date range based on saved date range or set default date range as last 6 months */
  useEffect(() => {
    if ((startValue && endValue) || dateSetRef.current) {
      return;
    }

    const isDateSet = () => {
      if (!savedDateRange?.selectedFilter) {
        return false;
      }
      switch (savedDateRange?.selectedFilter?.value) {
        case 'last12Months':
        case 'custom':
          return !!(last12Months?.dateRange?.startDate && last12Months?.dateRange?.endDate);
        case 'last90Days':
          return !!(last90Days?.dateRange?.startDate && last90Days?.dateRange?.endDate);
        case 'last30Days':
          return !!(last30Days?.dateRange?.startDate && last30Days?.dateRange?.endDate);
        default:
          return false;
      }
    };

    if (savedDateRange && savedDateRange.selectedFilter && isDateSet()) {
      selectedFilter.current = savedDateRange.selectedFilter;
      let savedStartDate, savedEndDate;

      if (['last12Months', 'custom'].includes(savedDateRange.selectedFilter.value)) {
        savedStartDate = last12Months?.dateRange?.startDate;
        savedEndDate = last12Months?.dateRange?.endDate;
      } else if (savedDateRange.selectedFilter.value === 'last90Days') {
        savedStartDate = last90Days?.dateRange?.startDate;
        savedEndDate = last90Days?.dateRange?.endDate;
      } else if (savedDateRange.selectedFilter.value === 'last30Days') {
        savedStartDate = last30Days?.dateRange?.startDate;
        savedEndDate = last30Days?.dateRange?.endDate;
      } else {
        savedStartDate = format(startDateDate, 'yyyy-MM-dd');
        savedEndDate = format(endDateDate, 'yyyy-MM-dd');
      }

      setStartDate(format(getDateFromString(savedStartDate), 'LLL d, yyyy'));
      setStartValue(savedStartDate);

      const formattedDayBeforToday = format(endDateDate, 'yyyy-MM-dd');

      if (
        ['last12Months', 'custom'].includes(savedDateRange.selectedFilter.value) &&
        !cacheEndDateUpdatedOnce.current &&
        savedEndDate !== formattedDayBeforToday
      ) {
        setEndDate(format(endDateDate, 'LLL d, yyyy'));
        setEndValue(endDateDate);
        dispatch(
          saveDateRange12Months({
            ...(last12Months?.dateRange ?? {}),
            endDate: formattedDayBeforToday,
          })
        );
        cacheEndDateUpdatedOnce.current = true;
      } else {
        setEndDate(format(getDateFromString(savedEndDate), 'LLL d, yyyy'));
        setEndValue(savedEndDate);
      }
    } else {
      setStartValue(startDateDate);
      setEndValue(endDateDate);
      hardRefresh.current = true;
      console.log(
        'initial date set',
        format(startDateDate, 'yyyy-MM-dd'),
        format(endDateDate, 'yyyy-MM-dd')
      );
      dispatch(
        saveCurrentDateRange({
          selectedFilter: {
            name: 'Custom',
            value: 'custom',
          },
        })
      );
      selectedFilter.current = {
        name: 'Custom',
        value: 'custom',
      };
      dispatch(
        saveDateRange12Months({
          /** using same redux for last 12 months and custom as data structure is same */
          ...(last12Months?.dateRange ?? {}),
          startDate: format(startDateDate, 'yyyy-MM-dd'),
          endDate: format(endDateDate, 'yyyy-MM-dd'),
        })
      );
      dateSetRef.current = true;
    }
  }, [savedDateRange, last12Months, last90Days, last30Days]);

  useEffect(() => {
    if (selectedCompany && selectedCompany.length === 1) {
      setCompanySelected(selectedCompany);
    }
  }, [selectedCompany]);

  function getPayload() {
    let tmpStartDate = format(getDateFromString(startValue), 'yyyy-MM-dd');
    let tmpEndDate = format(getDateFromString(endValue), 'yyyy-MM-dd');

    if (
      !hardRefresh.current &&
      savedDateRange?.selectedFilter &&
      ['custom', 'last12Months'].includes(savedDateRange?.selectedFilter?.value)
    ) {
      const { startDate: updatedStartDate, endDate: updatedEndDate } = getModifiedStartEndDate(
        last12Months?.dateRange || {},
        tmpStartDate,
        tmpEndDate
      );
      tmpStartDate = updatedStartDate;
      tmpEndDate = updatedEndDate;
    }
    return {
      companyIdList: companySelected.map((c) => c._id),
      startDate: tmpStartDate,
      endDate: tmpEndDate,
      viewedFrom: 'kpi',
      hardRefresh: hardRefresh.current,
      selectedFilter: selectedFilter.current,
    };
  }

  useEffect(() => {
    if (
      corporateList &&
      Array.isArray(corporateList.data) &&
      corporateList.data.length >= 1 &&
      companySelected.length === 0
    ) {
      let tmpCorporateList = [...corporateList.data];
      const ownCompany = corporateList.data.find((a) => a._id === auth.data.corporateId);
      if (ownCompany) {
        // If company owner is Wellness Coach, then show All Paid Company as company to get summary of all companies
        if (ownCompany.entity_name === 'Wellness Coach') {
          if (!selectedCompany) {
            setCompanySelected([{ _id: 'ALL', entity_name: 'All Paid Company', status: 1 }]);
          }
          tmpCorporateList.unshift({ _id: 'ALL', entity_name: 'All Paid Company', status: 1 });
        } else {
          setCompanySelected([ownCompany]);
        }
      } else {
        setCompanySelected(corporateList.data.slice(0, 1));
      }
      setCopyCorporateList(tmpCorporateList);
      const corpIdNameMap = {};
      tmpCorporateList.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveCropIdNameMap(corpIdNameMap));
    }
  }, [corporateList]);

  useEffect(() => {
    if (
      freemiumCorporateList &&
      Array.isArray(freemiumCorporateList.data) &&
      freemiumCorporateList.data.length >= 1
    ) {
      const corpIdNameMap = {};
      freemiumCorporateList.data.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveFreemiumCropIdNameMap(corpIdNameMap));
    }
  }, [freemiumCorporateList]);

  useEffect(() => {
    if (
      !Array.isArray(companySelected) ||
      companySelected.length !== 1 ||
      !startValue ||
      !endValue
    ) {
      return;
    }
    if (hardRefresh.current) {
      dispatch(resetKpiState());
    }
    dispatch(saveSelectedCompany(companySelected));

    /**
     * check data already exists for selectedFilterType within same date range
     */
    if (!hardRefresh.current) {
      if (['last12Months', 'custom'].includes(savedDateRange?.selectedFilter?.value)) {
        if (
          format(getDateFromString(startValue), 'yyyy-MM-dd') ===
            last12Months?.dateRange?.oldStartDate &&
          format(getDateFromString(endValue), 'yyyy-MM-dd') === last12Months?.dateRange?.oldEndDate
        ) {
          return;
        }
      } else if (savedDateRange?.selectedFilter?.value === 'last90Days') {
        if (
          format(getDateFromString(startValue), 'yyyy-MM-dd') ===
            last90Days?.dateRange?.oldStartDate &&
          format(getDateFromString(endValue), 'yyyy-MM-dd') === last90Days?.dateRange?.oldEndDate
        ) {
          return;
        }
      } else if (savedDateRange?.selectedFilter?.value === 'last30Days') {
        if (
          format(getDateFromString(startValue), 'yyyy-MM-dd') ===
            last30Days?.dateRange?.oldStartDate &&
          format(getDateFromString(endValue), 'yyyy-MM-dd') === last30Days?.dateRange?.oldEndDate
        ) {
          return;
        }
      }
    }
    fetchData();
  }, [companySelected, startValue, endValue]);

  function fetchData() {
    const payload = getPayload();
    payloadRef.current = payload;
    /**
     * save last fetched date range
     */
    if (['last12Months', 'custom'].includes(payload?.selectedFilter?.value)) {
      dispatch(
        saveDateRange12Months({
          ...(last12Months?.dateRange ?? {}),
          oldStartDate: format(getDateFromString(startValue), 'yyyy-MM-dd'),
          oldEndDate: format(getDateFromString(endValue), 'yyyy-MM-dd'),
        })
      );
    } else if (payload?.selectedFilter?.value === 'last90Days') {
      dispatch(
        saveDateRange90Days({
          ...(last90Days?.dateRange ?? {}),
          oldStartDate: format(getDateFromString(startValue), 'yyyy-MM-dd'),
          oldEndDate: format(getDateFromString(endValue), 'yyyy-MM-dd'),
        })
      );
    } else if (payload?.selectedFilter?.value === 'last30Days') {
      dispatch(
        saveDateRange30Days({
          ...(last30Days?.dateRange ?? {}),
          oldStartDate: format(getDateFromString(startValue), 'yyyy-MM-dd'),
          oldEndDate: format(getDateFromString(endValue), 'yyyy-MM-dd'),
        })
      );
    }

    /**
     * fetch data
     */

    let startDateIsLessThanEndDate = true;
    if (payload.startDate && payload.endDate) {
      startDateIsLessThanEndDate =
        new Date(payload.startDate).getTime() <= new Date(payload.endDate).getTime();
    }

    if (payload.companyIdList.length > 0 && startDateIsLessThanEndDate) {
      dispatch(getPaidRegisteredUsers(payload));
      dispatch(GetActiveUsers(payload));
      dispatch(fetchTotalUsersPlayedContent(payload));
      dispatch(fetchTotalMinsPlayed(payload));
      dispatch(fetchUsersRatedContent(payload));
      dispatch(fetchUsersFavoriteTheContent(payload));
      dispatch(fetchTotalStepsCaptured(payload));
      dispatch(fetchTotalUsersSyncingSteps(payload));
      dispatch(fetchNumberOfPcCompleted(payload));
      dispatch(fetchNumberOfPcBookings(payload));
      dispatch(fetchNumberOfUsersTakeWater(payload));
      dispatch(fetchTotalEarnedRewardPoints(payload));
      dispatch(fetchTotalRedeemedRewardPoints(payload));
      dispatch(fetchTotalUsersEarnedRewards(payload));
      dispatch(fetchTotalUsersRedeemedRewards(payload));
      dispatch(fetchTotalPushSent(payload));
      dispatch(fetchTotalChallengesLaunched(payload));
      dispatch(fetchTotalUsersJoinedInChallenge(payload));
      dispatch(fetchTotalUsersParticipatedInChallenge(payload));
      dispatch(fetchProgramJoined(payload));
      dispatch(fetchProgramCompleted(payload));
      dispatch(fetchBookRead(payload));
      dispatch(fetchTotalMealImagesUploaded(payload));
      dispatch(fetchTotalMealsAdded(payload));
      dispatch(fetchTotalAICoachMessages(payload));
      dispatch(fetchUniqueNutritionUsers(payload));
      hardRefresh.current = false;
    }
  }

  function getCorporateLogo(companySelected) {
    if (!companySelected[0]) {
      return '';
    }
    if (!companySelected[0]?.company_logo) {
      return (
        <div className='company-report--header--logo--no-logo-just-first-letter'>
          {companySelected[0].entity_name.charAt(0)}
        </div>
      );
    }
    return companySelected && companySelected[0]?.company_logo ? (
      <img src={companySelected[0].company_logo} alt='' />
    ) : (
      ''
    );
  }

  async function downloadOverallReport() {
    let startDate = format(new Date(startValue), 'yyyy-MM-dd');
    let endDate = format(new Date(endValue), 'yyyy-MM-dd');

    const response = await dispatch(
      downloadOverallReportForAllEnterprise({
        companyIdList: companySelected.map((c) => c._id),
        startDate: startDate,
        endDate: endDate,
        email: auth.data.email,
        viewedFrom: 'kpi',
      })
    );
    if (response.payload && response.payload.status === 200) {
      await info(
        'Your request has been received! You will receive an email shortly with the report.'
      );
    } else {
      toast.error('Something went wrong! Please try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
    }
  }

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilterType, sDate, eDate) => {
    setStartDate(format(sDate, 'LLL d, yyyy'));
    setStartValue(sDate);
    setEndDate(format(eDate, 'LLL d, yyyy'));
    setEndValue(eDate);
    selectedFilter.current = {
      ...(savedDateRange.selectedFilter || {}),
      ...selectedFilterType,
    };

    if (savedDateRange.quarterly) {
      hardRefresh.current = true;
    }

    if (['last12Months', 'custom'].includes(selectedFilterType.value)) {
      dispatch(
        saveDateRange12Months({
          ...(last12Months?.dateRange ?? {}),
          startDate: format(sDate, 'yyyy-MM-dd'),
          endDate: format(eDate, 'yyyy-MM-dd'),
        })
      );
    } else if (selectedFilterType.value === 'last90Days') {
      dispatch(
        saveDateRange90Days({
          ...(last90Days?.dateRange ?? {}),
          startDate: format(sDate, 'yyyy-MM-dd'),
          endDate: format(eDate, 'yyyy-MM-dd'),
        })
      );
    } else if (selectedFilterType.value === 'last30Days') {
      dispatch(
        saveDateRange30Days({
          ...(last30Days?.dateRange ?? {}),
          startDate: format(sDate, 'yyyy-MM-dd'),
          endDate: format(eDate, 'yyyy-MM-dd'),
        })
      );
    }

    dispatch(
      saveCurrentDateRange({
        ...savedDateRange,
        quarterly: false,
        selectedFilter: { ...selectedFilter.current },
      })
    );
  };

  const onClearKpiState = () => {
    dispatch(clearKpiState());
    hardRefresh.current = true;
    setCompanySelected([]);
    dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
    dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
  }

  return (
    <div className='container-fluid kpi'>
      <button onClick={downloadOverallReport} className='report-download btn btn-outline-dark'>
        Download Overall Report
      </button>
      <button
        onClick={() => {
          // dispatch(resetKpiState());
          hardRefresh.current = true;
          // fetchData();
          onClearKpiState();
        }}
        className='btn btn-primary hard-reload'
      >
        Refresh
      </button>
      <div className='company-report'>
        <div className='company-report--print'>
          <WCLogo />
          <div className='company-report--print--wc'>Bhartesh Coach</div>
          <div className='company-report--print--name'>{companySelected[0]?.entity_name}</div>
          <div className='company-report--print--dates'>
            {startDate} &ndash; {endDate}
          </div>
        </div>
        <div className='company-report--actions'>
          <div className='company-report--header'>
            <div className='company-report--header--logo'>{getCorporateLogo(companySelected)}</div>
            <div className='company-report--header--title'>
              <Typeahead
                id='some_id'
                onChange={(selected) => {
                  if (selected.length > 0) {
                    dispatch(resetKpiState());
                  }
                  hardRefresh.current = true;
                  setCompanySelected(selected);
                }}
                renderMenu={(results, menuProps) => {
                  let listWithActive = !copyCorporateList
                    ? []
                    : copyCorporateList.filter((c) => c.status === 1);

                  const results_len = results.length;
                  return (
                    <Menu {...menuProps} emptyLabel='No results found'>
                      {results.map((result, index) => (
                        <MenuItem option={result} position={index} key={index}>
                          <Highlighter search={menuProps.text}>{result.entity_name}</Highlighter>
                        </MenuItem>
                      ))}
                      {results_len > 0 && <Menu.Divider />}
                      {listWithActive.slice(0, 30).map(
                        (result, index) =>
                          result?._id !== companySelected[0]?._id && (
                            <MenuItem
                              option={result}
                              position={index + results_len + 1}
                              key={index + results_len + 1}
                            >
                              {result.entity_name}
                            </MenuItem>
                          )
                      )}
                      {listWithActive.length > 30 ? 'Please type name to see more ... ' : ''}
                    </Menu>
                  );
                }}
                className={
                  copyCorporateList && copyCorporateList.length === 1
                    ? 'company-report--header--single'
                    : ''
                }
                filterBy={['entity_name']}
                labelKey={'entity_name'}
                options={copyCorporateList ? copyCorporateList.filter((c) => c.status === 1) : []}
                selected={companySelected}
              />
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <div className='company-report--actions--date'>
              <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
                <Badge className='current-filter' bg='light' text='dark'>
                  {savedDateRange?.selectedFilter ? savedDateRange.selectedFilter.name : 'Custom'}
                </Badge>
                {startValue && endValue && (
                  <span className='date-range'>
                    {format(getDateFromString(startValue), 'MMM d')} -{' '}
                    {format(getDateFromString(endValue), 'MMM d, yyyy')}
                  </span>
                )}
                <span className='select-icon-down' aria-hidden='true'></span>
              </div>
              {showDateFilter && (
                <DateFilter
                  onClose={() => setShowDateFilter(false)}
                  startValue={startValue}
                  endValue={endValue}
                  onFilterUpdate={onFilterUpdate}
                  selectedFilter={savedDateRange?.selectedFilter}
                />
              )}
            </div>
            <div
              className={`company-report--actions--download${
                isDownloadInProgress() ? ' loading' : ''
              }`}
              onClick={() => downloadSheet()}
            >
              <img src={IconArrowGround} alt='' />
              <span>Download</span>
            </div>
          </div>
        </div>
        <h6 className='selected-enterprise'>Showing data for {companySelected[0]?.entity_name}</h6>
        <div id='data-table-container' className='data-table-container table-responsive'>
          {savedDateRange?.selectedFilter &&
          savedDateRange?.selectedFilter.value === 'last90Days' ? (
            <Last90DaysContainer
              companySelected={companySelected}
              startValue={startValue}
              endValue={endValue}
              selectedFilter={savedDateRange?.selectedFilter}
            />
          ) : savedDateRange?.selectedFilter &&
            savedDateRange?.selectedFilter.value === 'last30Days' ? (
            <Last30DaysContainer
              companySelected={companySelected}
              startValue={startValue}
              endValue={endValue}
              selectedFilter={savedDateRange?.selectedFilter}
            />
          ) : (
            <Last12MonthsContainer
              companySelected={companySelected}
              startValue={startValue ?? startDate}
              endValue={endValue ?? endDate}
              payload={payloadRef.current}
              selectedFilter={savedDateRange?.selectedFilter}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyKPIContainer;
